/**
 * Pure (no React) viewing-mode paint helpers for HtmlDescriptorAdapter.
 *
 * Ports the read-only-relevant subset of Containd's
 * `fullcomponents/template-system/src/components/rendering/adapterHelpers.ts`
 * (RN/Tamagui) to plain TS/CSS so the HTML twin paints the same values,
 * alignment, and chrome as `V2DescriptorAdapter` viewing mode.
 *
 * No host I/O, no RN/Tamagui imports — safe to unit test directly and to
 * mirror back into containd's `viewingPaintHelpers.ts` later.
 */
import type { CSSProperties } from 'react';
import type { RenderChrome } from '../../vendor/template-system/v2/runtime/renderer/types';
import type { TextFormat } from '../../vendor/template-system/v2/types/component';
import { getLucideName } from '../../vendor/template-system/v2/icons/iconCatalog';
import { resolveTemplateFontCssFamily } from '../../vendor/template-system/v2/types/templateFonts';
import { DEFAULT_STYLE_PADDING } from '../../vendor/template-system/v2/runtime/renderer/styleDefaults';

export { DEFAULT_STYLE_PADDING };

// ── Paint context (host theme injection) ──────────────────────────────────────

export interface PaintContext {
  locale?: string;
  primaryColor?: string;
  textColor?: string;
  mutedColor?: string;
  /**
   * Filled rating glyphs (stars / dots / bars / hearts).
   * Host `theme.ratingColor` / UI `warning` (default `#FF9500`).
   */
  ratingColor?: string;
  /** Empty rating glyphs when StyleBlock `color` is omitted. */
  ratingEmptyColor?: string;
  /** BCP-47 locale used for date component order (may differ from locale). */
  dateLocale?: string;
  hour12?: boolean;
  /** Navigate to an in-share item uuid from frozen text/button href item: links. */
  onItemLinkNavigate?: (itemUuid: string) => void;
  /**
   * Public-share web only. Default true (`_blank` for external and `item:` when
   * `itemLinkHref` returns a path). When false, even external URLs stay in this window.
   */
  openLinksInNewTab?: boolean;
  /** Resolve an in-share `item:` uuid to a share path for `<a href>`. */
  itemLinkHref?: (itemUuid: string) => string | null | undefined;
  /**
   * When set (Spaces freeze viewer), outbound clicks that need a leave-site
   * confirm call this instead of navigating. Omit for immediate open (tests).
   */
  onExternalLinkOpen?: (href: string) => void;
}

export type ItemLinkRender =
  | { mode: 'anchor'; href: string; target: '_blank'; rel: string }
  | { mode: 'spa'; ref: string };

export function resolveItemLinkRender(
  itemRef: string,
  paintContext?: PaintContext,
): ItemLinkRender {
  const href = paintContext?.itemLinkHref?.(itemRef)?.trim() || null;
  const openInNewTab = paintContext?.openLinksInNewTab !== false;
  if (openInNewTab && href) {
    return { mode: 'anchor', href, target: '_blank', rel: 'noopener noreferrer' };
  }
  return { mode: 'spa', ref: itemRef };
}

export type ResolvedPaintContext = Required<
  Omit<PaintContext, 'onExternalLinkOpen'>
> & {
  onExternalLinkOpen?: (href: string) => void;
};

/** Containd live `colors.warning` (iOS system orange). */
export const DEFAULT_RATING_COLOR = '#FF9500';
/** Host `DEFAULT_ADAPTER_THEME.ratingEmptyColor`. */
export const DEFAULT_RATING_EMPTY_COLOR = '#D1D5DB';

export const DEFAULT_PAINT_CONTEXT: ResolvedPaintContext = {
  locale: 'en',
  primaryColor: '#0a7ea4',
  textColor: '#1a1a1a',
  mutedColor: '#6b7280',
  ratingColor: DEFAULT_RATING_COLOR,
  ratingEmptyColor: DEFAULT_RATING_EMPTY_COLOR,
  dateLocale: 'en-US',
  hour12: true,
  onItemLinkNavigate: () => undefined,
  openLinksInNewTab: true,
  itemLinkHref: () => null,
  onExternalLinkOpen: undefined,
};

export function resolvePaintContext(paintContext?: PaintContext): ResolvedPaintContext {
  return {
    locale: paintContext?.locale ?? DEFAULT_PAINT_CONTEXT.locale,
    primaryColor: paintContext?.primaryColor ?? DEFAULT_PAINT_CONTEXT.primaryColor,
    textColor: paintContext?.textColor ?? DEFAULT_PAINT_CONTEXT.textColor,
    mutedColor: paintContext?.mutedColor ?? DEFAULT_PAINT_CONTEXT.mutedColor,
    ratingColor: paintContext?.ratingColor ?? DEFAULT_PAINT_CONTEXT.ratingColor,
    ratingEmptyColor:
      paintContext?.ratingEmptyColor ?? DEFAULT_PAINT_CONTEXT.ratingEmptyColor,
    dateLocale:
      paintContext?.dateLocale ??
      paintContext?.locale ??
      DEFAULT_PAINT_CONTEXT.dateLocale,
    hour12: paintContext?.hour12 ?? DEFAULT_PAINT_CONTEXT.hour12,
    onItemLinkNavigate:
      paintContext?.onItemLinkNavigate ?? DEFAULT_PAINT_CONTEXT.onItemLinkNavigate,
    openLinksInNewTab:
      paintContext?.openLinksInNewTab ?? DEFAULT_PAINT_CONTEXT.openLinksInNewTab,
    itemLinkHref: paintContext?.itemLinkHref ?? DEFAULT_PAINT_CONTEXT.itemLinkHref,
    onExternalLinkOpen: paintContext?.onExternalLinkOpen,
  };
}

// ── Localized text ─────────────────────────────────────────────────────────────

export type LocalizedTextLike = string | Record<string, string> | null | undefined;

export function resolveLocalizedTextSimple(
  text: LocalizedTextLike,
  locale: string,
  fallback = '',
): string {
  if (text == null) return fallback;
  if (typeof text === 'string') return text;
  const byLocale = text[locale];
  if (typeof byLocale === 'string' && byLocale.length > 0) return byLocale;
  const en = text.en;
  if (typeof en === 'string' && en.length > 0) return en;
  const first = Object.values(text).find((v) => typeof v === 'string' && v.length > 0);
  return first ?? fallback;
}

// ── Text value bookkeeping ─────────────────────────────────────────────────────

export function isEmptyBoundText(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  return String(value).trim() === '';
}

/**
 * True when a string looks like an ISO 8601 date or date-time. Requires a
 * YYYY-MM-DD prefix — bare `Date.parse` accepts many false positives.
 */
export function looksLikeIsoDateTime(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  const raw = String(value).trim();
  if (
    !/^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/i.test(raw)
  ) {
    return false;
  }
  return !Number.isNaN(Date.parse(raw));
}

/** Maps TextFormat.dateFormat to formatTemplateDateValue presets. */
export function resolveTextDateFormatHint(dateFormat?: string): 'date' | 'time' | undefined {
  if (!dateFormat || dateFormat.trim() === '') return undefined;
  const lower = dateFormat.toLowerCase();
  if (lower === 'date' || (lower.includes('yyyy') && !lower.includes('hh') && !lower.includes('mm:ss'))) {
    return 'date';
  }
  if (lower === 'time' || lower.includes('hh:mm')) return 'time';
  return undefined;
}

export function formatTemplateDateValue(
  value: unknown,
  format?: string,
  localeOrOptions?: string | { locale?: string; dateLocale?: string; hour12?: boolean },
): string {
  if (value === null || value === undefined || value === '') return '';
  const raw = String(value);
  const parsed = Date.parse(raw);
  if (Number.isNaN(parsed)) return raw;
  const date = new Date(parsed);
  const opts =
    typeof localeOrOptions === 'string'
      ? { locale: localeOrOptions, dateLocale: localeOrOptions, hour12: false }
      : {
          locale: localeOrOptions?.locale ?? 'en-US',
          dateLocale: localeOrOptions?.dateLocale ?? localeOrOptions?.locale ?? 'en-US',
          hour12: localeOrOptions?.hour12 ?? false,
        };
  const dateOpts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  };
  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: opts.hour12,
  };
  if (format === 'date') {
    return new Intl.DateTimeFormat(opts.dateLocale, dateOpts).format(date);
  }
  if (format === 'time') {
    return new Intl.DateTimeFormat(opts.locale, timeOpts).format(date);
  }
  return `${new Intl.DateTimeFormat(opts.dateLocale, dateOpts).format(date)} ${new Intl.DateTimeFormat(opts.locale, timeOpts).format(date)}`;
}

/** Clamp authored maxDigits into the Intl-safe 0–20 range; undefined when unset/invalid. */
export function normalizeMaxDigits(maxDigits: number | null | undefined): number | undefined {
  if (maxDigits == null || !Number.isFinite(maxDigits)) return undefined;
  return Math.min(20, Math.max(0, Math.floor(maxDigits)));
}

function formatNumberBody(
  value: unknown,
  numberFormat: TextFormat['numberFormat'] | undefined,
  locale: string | undefined,
  maxDigits: number | undefined,
): string {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  const resolvedLocale = locale && locale.length > 0 ? locale : undefined;
  switch (numberFormat) {
    case 'integer':
      return new Intl.NumberFormat(resolvedLocale, { maximumFractionDigits: 0 }).format(
        Math.round(numeric),
      );
    case 'percent':
      return new Intl.NumberFormat(resolvedLocale, {
        style: 'percent',
        maximumFractionDigits: maxDigits ?? 2,
      }).format(numeric);
    case 'currency':
      return new Intl.NumberFormat(resolvedLocale, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: maxDigits ?? 2,
      }).format(numeric);
    case 'decimal':
      return new Intl.NumberFormat(resolvedLocale, {
        maximumFractionDigits: maxDigits ?? 6,
      }).format(numeric);
    default:
      if (maxDigits !== undefined) {
        return new Intl.NumberFormat(resolvedLocale, { maximumFractionDigits: maxDigits }).format(
          numeric,
        );
      }
      return String(numeric);
  }
}

/** Apply `%s` / `{0}` pattern, else prefix/suffix, from a TextFormat. No-op when unset. */
export function applyTextFormatPattern(
  body: string,
  format?: Pick<TextFormat, 'pattern' | 'prefix' | 'suffix'> | null,
): string {
  if (!format) return body;
  const pattern = format.pattern;
  if (typeof pattern === 'string' && pattern.length > 0) {
    return pattern.replace('%s', body).replace('{0}', body);
  }
  const prefix = format.prefix ?? '';
  const suffix = format.suffix ?? '';
  if (!prefix && !suffix) return body;
  return `${prefix}${body}${suffix}`;
}

export interface FormatTextDisplayValueOptions {
  format?: TextFormat | null;
  valueKind?: 'datetime' | 'number';
  locale?: string;
  includeAffixes?: boolean;
  maxDigits?: number | null;
  dateTimeOptions?: { locale?: string; dateLocale?: string; hour12?: boolean } | null;
}

/** Formats a text/number/date-ish value the same way as the RN adapter's TextNode. */
export function formatTextDisplayValue(
  value: unknown,
  options?: FormatTextDisplayValueOptions,
): string {
  if (isEmptyBoundText(value)) return '';

  let body: string;
  const dateHint = resolveTextDateFormatHint(options?.format?.dateFormat);
  const dateOpts = options?.dateTimeOptions ?? options?.locale;
  if (options?.valueKind === 'number' || options?.format?.numberFormat !== undefined) {
    body = formatNumberBody(
      value,
      options?.format?.numberFormat,
      options?.locale,
      normalizeMaxDigits(options?.maxDigits),
    );
  } else if (options?.valueKind === 'datetime' || dateHint !== undefined) {
    body = formatTemplateDateValue(value, dateHint, dateOpts);
  } else if (looksLikeIsoDateTime(value)) {
    body = formatTemplateDateValue(value, undefined, dateOpts);
  } else {
    body = String(value);
  }

  if (options?.includeAffixes === false) return body;
  return applyTextFormatPattern(body, options?.format ?? undefined);
}

// ── Node display text resolution (bug #1/#2/#3/#9/#10/#13) ────────────────────

function defaultDisplayValue(props: Record<string, unknown>): string {
  const v = props.value ?? props.label ?? props.text ?? props.name ?? '';
  if (v == null) return '';
  if (typeof v === 'object' && v !== null && 'en' in (v as object)) {
    return String((v as { en?: string }).en ?? '');
  }
  return String(v);
}

/**
 * Resolves the string a node should paint. Always prefers a host-baked
 * `props.displayText` (already formatted with affixes) when present, then
 * falls back to type-specific formatting from raw prop values.
 */
export function resolveNodeDisplayText(
  type: string,
  props: Record<string, unknown>,
  paintContext?: PaintContext,
): string {
  const baked = props.displayText;
  if (typeof baked === 'string' && baked.length > 0) return baked;

  const locale = resolvePaintContext(paintContext).locale;
  const paint = resolvePaintContext(paintContext);

  switch (type) {
    case 'text': {
      const value = props.value;
      if (isEmptyBoundText(value)) return '';
      return formatTextDisplayValue(value, {
        format: (props.format as TextFormat | null | undefined) ?? undefined,
        valueKind: props.valueKind === 'datetime' ? 'datetime' : undefined,
        locale,
        includeAffixes: true,
        dateTimeOptions: {
          locale: paint.locale,
          dateLocale: paint.dateLocale,
          hour12: paint.hour12,
        },
      });
    }
    case 'number': {
      const value = props.value;
      if (value === null || value === undefined) return '';
      return formatTextDisplayValue(value, {
        format: (props.format as TextFormat | null | undefined) ?? undefined,
        valueKind: 'number',
        locale,
        includeAffixes: true,
        maxDigits: props.maxDigits as number | null | undefined,
      });
    }
    case 'date': {
      const value = props.value;
      if (value === null || value === undefined || String(value).length === 0) return '—';
      return formatTemplateDateValue(value, props.format as string | undefined, {
        locale: paint.locale,
        dateLocale: paint.dateLocale,
        hour12: paint.hour12,
      });
    }
    case 'stringSelect': {
      const selected = props.selected;
      return selected !== null && selected !== undefined ? String(selected) : '';
    }
    case 'enum': {
      const value = props.value;
      const raw = value !== null && value !== undefined ? String(value) : '';
      const enumValues = Array.isArray(props.enumValues)
        ? (props.enumValues as Array<{ id: string; displayName?: LocalizedTextLike }>)
        : [];
      const matched = enumValues.find((entry) => entry.id === raw);
      if (matched) return resolveLocalizedTextSimple(matched.displayName, locale, raw);
      return raw;
    }
    case 'timerDisplay':
      return String(props.formatted ?? props.display ?? '');
    default:
      return defaultDisplayValue(props);
  }
}

// ── Color helpers ──────────────────────────────────────────────────────────────

/** Strips alpha from #RRGGBBAA / rgba so foreground text/icons stay fully opaque. */
export function resolveOpaqueForegroundColor(color: string | undefined, fallback: string): string {
  if (!color) return fallback;
  const raw = color.trim();
  const hex8 = /^#([0-9a-fA-F]{8})$/.exec(raw);
  if (hex8) return `#${hex8[1].slice(0, 6)}`;
  const rgba = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/.exec(raw);
  if (rgba) {
    const r = Math.round(Number(rgba[1]));
    const g = Math.round(Number(rgba[2]));
    const b = Math.round(Number(rgba[3]));
    return `rgb(${r}, ${g}, ${b})`;
  }
  return raw;
}

export function parseHexColor(raw: string | undefined): { r: number; g: number; b: number } | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  const short = /^#([0-9a-fA-F]{3})$/.exec(trimmed);
  if (short) {
    const h = short[1]!;
    return {
      r: parseInt(h[0]! + h[0]!, 16),
      g: parseInt(h[1]! + h[1]!, 16),
      b: parseInt(h[2]! + h[2]!, 16),
    };
  }
  const full = /^#([0-9a-fA-F]{6})([0-9a-fA-F]{2})?$/.exec(trimmed);
  if (full) {
    const h = full[1]!;
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }
  return null;
}

function channelToHex(n: number): string {
  return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
}

const BEVEL_FALLBACK_BASE = '#64748b';

function mixHexToward(
  hex: string | undefined,
  target: { r: number; g: number; b: number },
  t: number,
  fallback: string = BEVEL_FALLBACK_BASE,
): string {
  const parsed = parseHexColor(hex) ?? parseHexColor(fallback);
  if (parsed == null) return fallback;
  const amount = Math.max(0, Math.min(1, t));
  const r = parsed.r + (target.r - parsed.r) * amount;
  const g = parsed.g + (target.g - parsed.g) * amount;
  const b = parsed.b + (target.b - parsed.b) * amount;
  return `#${channelToHex(r)}${channelToHex(g)}${channelToHex(b)}`;
}

export function lightenHex(hex: string | undefined, amount = 0.18): string {
  return mixHexToward(hex, { r: 255, g: 255, b: 255 }, amount);
}

export function darkenHex(hex: string | undefined, amount = 0.22): string {
  return mixHexToward(hex, { r: 0, g: 0, b: 0 }, amount);
}

export type TemplateBevel = 'raised' | 'inset';

export interface BevelEdgeColors {
  topLeft: string;
  bottomRight: string;
}

export function resolveBevelEdgeColors(
  bevel: TemplateBevel | undefined,
  baseColor: string | undefined,
): BevelEdgeColors | undefined {
  if (bevel !== 'raised' && bevel !== 'inset') return undefined;
  const light = lightenHex(baseColor, 0.18);
  const dark = darkenHex(baseColor, 0.22);
  return bevel === 'raised' ? { topLeft: light, bottomRight: dark } : { topLeft: dark, bottomRight: light };
}

/** Base tone for bevel derivation: opaque background, else borderColor, else fallback. */
export function resolveBevelBaseColor(
  chrome: Pick<RenderChrome, 'backgroundColor' | 'borderColor'> | undefined,
  fallback: string = BEVEL_FALLBACK_BASE,
): string {
  const bg = chrome?.backgroundColor;
  if (typeof bg === 'string' && bg.length > 0 && bg !== 'transparent' && parseHexColor(bg) != null) {
    return bg.length === 9 ? `#${bg.slice(1, 7)}` : bg;
  }
  const border = chrome?.borderColor;
  if (
    typeof border === 'string' &&
    border.length > 0 &&
    border !== 'transparent' &&
    parseHexColor(border) != null
  ) {
    return border.length === 9 ? `#${border.slice(1, 7)}` : border;
  }
  return fallback;
}

/** Effective border width for bevel: missing → 1; explicit 0 → 0 (no ring). */
export function effectiveBevelBorderWidth(
  bevel: TemplateBevel | undefined,
  authoredWidth: number | undefined,
): number | undefined {
  if (bevel !== 'raised' && bevel !== 'inset') return authoredWidth;
  if (authoredWidth === undefined || !Number.isFinite(authoredWidth)) return 1;
  return authoredWidth;
}

export interface BevelBorderCss {
  borderWidth?: number;
  borderTopColor?: string;
  borderLeftColor?: string;
  borderBottomColor?: string;
  borderRightColor?: string;
}

/**
 * Per-side border CSS for bevel. Raised = light top-left / dark bottom-right;
 * inset = inverted. Missing/0 width and inactive bevel both return `{}`.
 */
export function bevelBorderCss(
  bevel: TemplateBevel | undefined,
  baseColor: string | undefined,
  borderWidth: number | undefined,
): BevelBorderCss {
  const edges = resolveBevelEdgeColors(bevel, baseColor);
  if (edges == null) return {};
  const width = borderWidth === undefined ? 1 : Number.isFinite(borderWidth) ? borderWidth : 0;
  if (width <= 0) return {};
  return {
    borderWidth: width,
    borderTopColor: edges.topLeft,
    borderLeftColor: edges.topLeft,
    borderBottomColor: edges.bottomRight,
    borderRightColor: edges.bottomRight,
  };
}

// ── Button chrome (bug #7) ──────────────────────────────────────────────────────

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonVariantColors {
  backgroundColor: string;
  labelColor: string;
}

export function resolveButtonVariantColors(
  variant: ButtonVariant | undefined,
  theme: { primaryColor: string; textColor: string },
): ButtonVariantColors {
  switch (variant ?? 'primary') {
    case 'secondary':
      return { backgroundColor: `${theme.primaryColor}22`, labelColor: theme.textColor };
    case 'ghost':
      return { backgroundColor: 'transparent', labelColor: theme.primaryColor };
    case 'danger':
      return { backgroundColor: '#EF4444', labelColor: '#ffffff' };
    case 'primary':
    default:
      return { backgroundColor: theme.primaryColor, labelColor: '#ffffff' };
  }
}

export interface ButtonChromeBorder {
  borderWidth: number;
  borderColor: string;
}

/** Button fill-surface border. Ghost keeps a 1px primary outline when unstyled. */
export function resolveButtonChromeBorder(
  chrome: Pick<RenderChrome, 'borderWidth' | 'borderColor' | 'backgroundColor'> | undefined,
  variant: ButtonVariant | undefined,
  theme: { primaryColor: string },
): ButtonChromeBorder {
  const chromeWidth =
    typeof chrome?.borderWidth === 'number' && Number.isFinite(chrome.borderWidth) ? chrome.borderWidth : 0;
  const chromeColor = typeof chrome?.borderColor === 'string' ? chrome.borderColor : 'transparent';
  const isGhost = (variant ?? 'primary') === 'ghost';
  const ghostFallback = isGhost && chrome?.backgroundColor == null && chromeWidth === 0;
  if (ghostFallback) return { borderWidth: 1, borderColor: theme.primaryColor };
  return { borderWidth: chromeWidth, borderColor: chromeColor };
}

export interface ButtonChromeCss {
  backgroundColor: string;
  color: string;
  borderRadius: number;
  borderWidth: number;
  borderStyle?: 'solid' | 'none';
  /** Shorthand reset so UA `<button>` chrome cannot combine with color-only longhands. */
  border?: 'none';
  borderColor?: string;
  borderTopColor?: string;
  borderLeftColor?: string;
  borderBottomColor?: string;
  borderRightColor?: string;
}

function noButtonRingCss(): Pick<ButtonChromeCss, 'borderWidth' | 'borderStyle' | 'border'> {
  return { borderWidth: 0, borderStyle: 'none', border: 'none' };
}

/**
 * Full button fill-surface CSS: variant colors, bevel (raised/inset) or flat
 * border, no ring when `chrome.borderWidth === 0` and no bevel is set.
 */
export function resolveButtonChromeCss(
  variant: ButtonVariant | undefined,
  chrome: RenderChrome | undefined,
  theme: { primaryColor: string; textColor: string },
): ButtonChromeCss {
  const variantColors = resolveButtonVariantColors(variant, theme);
  const backgroundColor = chrome?.backgroundColor ?? variantColors.backgroundColor;
  const color = resolveOpaqueForegroundColor(chrome?.color, variantColors.labelColor);
  const borderRadius = chrome?.borderRadius ?? 6;
  const bevel = chrome?.bevel;
  const bevelActive = bevel === 'raised' || bevel === 'inset';
  const flatBorder = resolveButtonChromeBorder(chrome, variant, theme);

  if (bevelActive) {
    const baseColor = resolveBevelBaseColor({
      backgroundColor: backgroundColor === 'transparent' ? undefined : backgroundColor,
      borderColor: chrome?.borderColor,
    });
    const width =
      chrome?.borderWidth === 0
        ? 0
        : effectiveBevelBorderWidth(bevel, flatBorder.borderWidth > 0 ? flatBorder.borderWidth : undefined);
    const bevel_ = bevelBorderCss(bevel, baseColor, width);
    if ((bevel_.borderWidth ?? 0) <= 0) {
      return {
        backgroundColor,
        color,
        borderRadius,
        ...noButtonRingCss(),
      };
    }
    return {
      backgroundColor,
      color,
      borderRadius,
      borderWidth: bevel_.borderWidth ?? 0,
      borderStyle: 'solid',
      borderTopColor: bevel_.borderTopColor,
      borderLeftColor: bevel_.borderLeftColor,
      borderBottomColor: bevel_.borderBottomColor,
      borderRightColor: bevel_.borderRightColor,
    };
  }

  if (flatBorder.borderWidth <= 0) {
    return {
      backgroundColor,
      color,
      borderRadius,
      ...noButtonRingCss(),
    };
  }

  return {
    backgroundColor,
    color,
    borderRadius,
    borderWidth: flatBorder.borderWidth,
    borderStyle: 'solid',
    borderColor: flatBorder.borderColor,
  };
}

// ── Text alignment (bug #1) ─────────────────────────────────────────────────────

export type FlexAlign = 'flex-start' | 'center' | 'flex-end';

/** `chrome.verticalAlign` → flex `justifyContent` on the vertical stack axis. */
export function resolveVerticalJustifyContent(verticalAlign: RenderChrome['verticalAlign']): FlexAlign {
  if (verticalAlign === 'center') return 'center';
  if (verticalAlign === 'bottom') return 'flex-end';
  return 'flex-start';
}

/** `chrome.textAlign` → wrapper `alignItems` on the horizontal axis (left/center/right). */
export function resolveTextAlignItems(textAlign: RenderChrome['textAlign']): FlexAlign {
  if (textAlign === 'center') return 'center';
  if (textAlign === 'right') return 'flex-end';
  return 'flex-start';
}

// ── Line (bug #5) ────────────────────────────────────────────────────────────────

export interface LineProps {
  orientation?: 'horizontal' | 'vertical';
  strokeWidthPx?: number | null;
  colorValue?: string | null;
}

export interface LineStrokeStyle {
  width: string | number;
  height: string | number;
  backgroundColor: string;
}

/** Centered thin bar style for the `line` node — never fills the full layout box. */
export function lineStrokeStyle(props: LineProps, fallbackColor = '#999999'): LineStrokeStyle {
  const strokeColor =
    typeof props.colorValue === 'string' && props.colorValue.length > 0 ? props.colorValue : fallbackColor;
  const thickness =
    typeof props.strokeWidthPx === 'number' && props.strokeWidthPx > 0 ? props.strokeWidthPx : 2;
  const isVertical = props.orientation === 'vertical';
  return {
    width: isVertical ? thickness : '100%',
    height: isVertical ? '100%' : thickness,
    backgroundColor: strokeColor,
  };
}

// ── Drag handle (bug #8) ─────────────────────────────────────────────────────────

const DRAG_HANDLE_DOT_ROWS = 3;

/**
 * Dot diameter for the 2×3 drag-handle grid. Ports Containd's
 * `computeDragHandleDotSize` (byWidth = w*0.12, byHeight = h/(rows*2.2),
 * clamped to [2, fontSize*0.28]).
 */
export function computeDragHandleDotSize(
  containerWidth: number,
  containerHeight: number,
  minFontSize: number,
): number {
  if (containerWidth <= 0 || containerHeight <= 0) {
    return Math.max(2, minFontSize * 0.22);
  }
  const byWidth = containerWidth * 0.12;
  const byHeight = containerHeight / (DRAG_HANDLE_DOT_ROWS * 2.2);
  return Math.max(2, Math.min(minFontSize * 0.28, byWidth, byHeight));
}

/** Match Containd `computeRatingStarSize` (adapterHelpers). */
export const RATING_STAR_MIN_SIZE = 8;
export const RATING_STAR_GAP = 2;

/**
 * Empty glyph uses style.color; filled glyphs always use theme.ratingColor
 * (style.backgroundColor is layout chrome only). Match Containd
 * `resolveRatingStarColors`.
 */
export function resolveRatingStarColors(
  chrome: RenderChrome | undefined,
  theme: { ratingColor: string; ratingEmptyColor: string },
): { filledColor: string; emptyColor: string } {
  return {
    emptyColor: chrome?.color ?? theme.ratingEmptyColor,
    filledColor: theme.ratingColor,
  };
}

export function computeRatingStarSize(
  containerWidth: number,
  containerHeight: number,
  maxStars: number,
  minFontSize: number,
): number {
  const minSize = Math.max(RATING_STAR_MIN_SIZE, minFontSize);
  if (maxStars <= 0) {
    return minSize;
  }
  if (containerWidth <= 0 || containerHeight <= 0) {
    return minSize;
  }
  const fillByWidth =
    (containerWidth - RATING_STAR_GAP * (maxStars - 1)) / maxStars;
  const fillByHeight = containerHeight;
  const fillSize = Math.min(fillByWidth, fillByHeight);
  return Math.max(minSize, Math.floor(fillSize));
}

// ── SVG (bug #4) ─────────────────────────────────────────────────────────────────

function svgPreserveAspectRatio(fit: 'contain' | 'cover' | 'fill' | undefined): string {
  if (fit === 'fill') return 'none';
  if (fit === 'cover') return 'xMidYMid slice';
  return 'xMidYMid meet';
}

/** Browser data URI for inline SVG markup (scripts do not run in SVG-as-image). */
export function svgMarkupToDataUri(markup: string, mediaType = 'image/svg+xml'): string {
  const trimmed = markup.trimStart();
  if (trimmed.startsWith('<')) {
    return `data:${mediaType};charset=utf-8,${encodeURIComponent(markup)}`;
  }
  return `data:${mediaType};base64,${markup}`;
}

export function svgFitToObjectFit(
  fit: 'contain' | 'cover' | 'fill' | undefined,
): 'contain' | 'cover' | 'fill' {
  if (fit === 'cover' || fit === 'fill') return fit;
  return 'contain';
}

/**
 * Forces the root `<svg>` tag to fill its container and honor `fit`, so a raw
 * `svgContent` string encoded as an `<img>` data URI scales like RN's
 * `SvgXml` (which is handed explicit width/height/preserveAspectRatio props).
 */
export function resolveSvgSizingMarkup(xml: string, fit: 'contain' | 'cover' | 'fill' | undefined): string {
  if (!xml.includes('<svg')) return xml;
  const preserveAspectRatio = svgPreserveAspectRatio(fit);
  return xml.replace(/<svg([^>]*)>/i, (full, attrs: string) => {
    let next = attrs
      .replace(/\swidth\s*=\s*(["'])[^"']*\1/i, '')
      .replace(/\sheight\s*=\s*(["'])[^"']*\1/i, '')
      .replace(/\spreserveAspectRatio\s*=\s*(["'])[^"']*\1/i, '');
    next += ` width="100%" height="100%" preserveAspectRatio="${preserveAspectRatio}"`;
    return `<svg${next}>`;
  });
}

// ── Icon (bug #6) ────────────────────────────────────────────────────────────────

/** Lucide PascalCase export name for a catalog icon id, or undefined if unknown. */
export function resolveLucideIconName(name: string | null | undefined): string | undefined {
  if (!name) return undefined;
  return getLucideName(name);
}

// ── Rating (bug #11) ─────────────────────────────────────────────────────────────

export function coerceRatingValue(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value);
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return Math.round(parsed);
  }
  return 0;
}

// ── Boolean (bug #14) ────────────────────────────────────────────────────────────

export interface BooleanControlColors {
  onFill: string;
  offFill: string;
  outline: string;
  contrastOn: string;
}

export function resolveBooleanControlColors(
  colors: { trueColor?: string | null; falseColor?: string | null; outlineColor?: string | null } | undefined,
  theme: { primaryColor: string; mutedColor: string },
): BooleanControlColors {
  const onFill = resolveOpaqueForegroundColor(colors?.trueColor ?? undefined, theme.primaryColor);
  const offFill = colors?.falseColor ? resolveOpaqueForegroundColor(colors.falseColor, 'transparent') : 'transparent';
  const outline = resolveOpaqueForegroundColor(colors?.outlineColor ?? undefined, theme.mutedColor);
  return { onFill, offFill, outline, contrastOn: '#ffffff' };
}

export const BOOLEAN_CONTROL_MIN_SIZE = 14;

export function computeBooleanControlSizeFromFont(fontSize: number): number {
  return Math.max(BOOLEAN_CONTROL_MIN_SIZE, Math.round(fontSize * 1.15));
}

export function computeBooleanCheckboxFillSize(
  containerWidth: number,
  containerHeight: number,
  minSize: number = BOOLEAN_CONTROL_MIN_SIZE,
): number {
  if (containerWidth <= 0 || containerHeight <= 0) {
    return minSize;
  }
  return Math.max(minSize, Math.floor(Math.min(containerWidth, containerHeight)));
}

export interface BooleanSwitchFillSize {
  trackWidth: number;
  trackHeight: number;
  thumbSize: number;
}

export function computeBooleanSwitchFillSize(
  containerWidth: number,
  containerHeight: number,
  minHeight: number = BOOLEAN_CONTROL_MIN_SIZE,
): BooleanSwitchFillSize {
  const trackHeight =
    containerWidth <= 0 || containerHeight <= 0
      ? minHeight
      : Math.max(minHeight, Math.floor(Math.min(containerHeight, containerWidth * 0.55)));
  const trackWidth =
    containerWidth <= 0
      ? Math.round(trackHeight * 1.75)
      : Math.max(Math.round(trackHeight * 1.75), Math.floor(containerWidth));
  const thumbSize = Math.max(BOOLEAN_CONTROL_MIN_SIZE - 2, Math.floor(trackHeight * 0.82));
  return { trackWidth, trackHeight, thumbSize };
}

export function computeBooleanSwitchSizeFromFont(fontSize: number): BooleanSwitchFillSize {
  const trackHeight = computeBooleanControlSizeFromFont(fontSize);
  const trackWidth = Math.round(trackHeight * 1.75);
  const thumbSize = Math.max(BOOLEAN_CONTROL_MIN_SIZE - 2, Math.floor(trackHeight * 0.82));
  return { trackWidth, trackHeight, thumbSize };
}

export type BooleanLabelPlacement = 'trailing' | 'sides' | 'none';

export function resolveBooleanLabelPlacement(
  placement: 'trailing' | 'sides' | null | undefined,
  variant: 'toggle' | 'checkbox' | 'icon' | null | undefined,
  hasAnyLabel: boolean,
): BooleanLabelPlacement {
  if (!hasAnyLabel) {
    return 'none';
  }
  if (variant === 'toggle' && placement === 'sides') {
    return 'sides';
  }
  return 'trailing';
}

// ── Chrome → CSS (bug #1 fix lives here) ────────────────────────────────────────

/**
 * Visible line cap for template text. Explicit maxLines wins; wrap/multiline
 * leave the cap unset so the string can continue onto further lines.
 * Match Containd `resolveTextNumberOfLines`.
 */
export function resolveTextNumberOfLines(
  maxLines?: number | null,
  multiline?: boolean | null,
  overflow?: 'clip' | 'ellipsis' | 'ellipsisStart' | 'wrap' | null,
): number | undefined {
  if (typeof maxLines === 'number' && maxLines > 0) {
    return maxLines;
  }
  if (overflow === 'wrap' || multiline) {
    return undefined;
  }
  return 1;
}

export function defaultStylePaddingCss(): string {
  return `${DEFAULT_STYLE_PADDING.top}px ${DEFAULT_STYLE_PADDING.right}px ${DEFAULT_STYLE_PADDING.bottom}px ${DEFAULT_STYLE_PADDING.left}px`;
}

export function resolvePaddingEdgesPx(
  padding: RenderChrome['padding'] | undefined,
): { top: number; bottom: number; left: number; right: number } {
  if (padding === undefined) {
    return {
      top: DEFAULT_STYLE_PADDING.top,
      bottom: DEFAULT_STYLE_PADDING.bottom,
      left: DEFAULT_STYLE_PADDING.left,
      right: DEFAULT_STYLE_PADDING.right,
    };
  }
  if (typeof padding === 'number') {
    return { top: padding, bottom: padding, left: padding, right: padding };
  }
  return {
    top: padding.top ?? 0,
    bottom: padding.bottom ?? 0,
    left: padding.left ?? 0,
    right: padding.right ?? 0,
  };
}

/**
 * Subtracts StyleBlock padding from a layout box so fill-sized controls
 * (boolean, rating) scale to the inner content area, not the outer chrome box.
 */
export function insetBoxByPadding(
  width: number,
  height: number,
  padding: RenderChrome['padding'] | undefined,
): { width: number; height: number } {
  const edges = resolvePaddingEdgesPx(padding);
  return {
    width: Math.max(0, width - edges.left - edges.right),
    height: Math.max(0, height - edges.top - edges.bottom),
  };
}

export interface ChromeCssResult {
  style: CSSProperties;
  /** Wrapper `alignItems` derived from `textAlign` (left/center/right). */
  alignItems: FlexAlign;
}

/**
 * Maps `RenderChrome` to CSS. Fixes the viewing-mode alignment bug:
 * `verticalAlign` drives `justifyContent` (not `alignItems`), and
 * `textAlign` drives both CSS `textAlign` and the wrapper `alignItems`.
 */
/** Maps `RenderChrome.padding` (number | per-side object) to a CSS shorthand value. */
export function chromePaddingToCss(
  padding: RenderChrome['padding'] | undefined,
  fallback?: string | number,
): string | number | undefined {
  if (padding == null) return fallback;
  if (typeof padding === 'number') return padding;
  return `${padding.top ?? 0}px ${padding.right ?? 0}px ${padding.bottom ?? 0}px ${padding.left ?? 0}px`;
}

export function chromeFontWeightToCss(
  fontWeight: RenderChrome['fontWeight'] | undefined,
): CSSProperties['fontWeight'] {
  if (fontWeight === 'bold' || fontWeight === 'semibold') return 700;
  if (fontWeight === 'light') return 300;
  if (fontWeight === 'normal') return 400;
  return undefined;
}

/** Head-ellipsis (RN `ellipsizeMode: 'head'`) via RTL overflow trick. */
export function ellipsisStartCss(): CSSProperties {
  return {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    direction: 'rtl',
    unicodeBidi: 'plaintext',
  };
}

/**
 * Link `<a>` underline only when chrome did not already set a decoration
 * (or set `none`). Chrome underline/strike is preserved.
 */
export function resolveLinkTextDecoration(
  chromeDecoration: CSSProperties['textDecoration'],
  isLink: boolean,
): CSSProperties['textDecoration'] {
  if (!isLink) return chromeDecoration;
  const dec = typeof chromeDecoration === 'string' ? chromeDecoration : undefined;
  if (!dec || dec === 'none') return 'underline';
  if (dec === 'line-through') return 'underline line-through';
  return chromeDecoration;
}

export function chromeToCss(chrome?: RenderChrome): ChromeCssResult {
  // Never set width/height here — NodeView merges this AFTER layoutToCss.
  // RN puts flex:1 / 100% on an *inner* Stack; HTML uses one box, so 100%
  // would clobber fractional layout.{width,height} and every node fills the card.
  if (!chrome) {
    return {
      style: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        boxSizing: 'border-box',
        padding: defaultStylePaddingCss(),
      },
      alignItems: 'flex-start',
    };
  }
  const padding = chromePaddingToCss(chrome.padding, defaultStylePaddingCss());
  const fontCss = resolveTemplateFontCssFamily(chrome.fontFamily);
  const fontFamily = fontCss.includes(',')
    ? fontCss
    : `${fontCss}, 'Source Sans 3', system-ui, sans-serif`;
  const style: CSSProperties = {
    color: chrome.color,
    backgroundColor: chrome.backgroundColor,
    fontSize: chrome.fontSize,
    fontFamily,
    fontWeight: chromeFontWeightToCss(chrome.fontWeight) ?? 400,
    fontStyle: chrome.fontStyle,
    textAlign: chrome.textAlign,
    textDecoration: chrome.textDecoration,
    borderColor: chrome.borderColor,
    borderWidth: chrome.borderWidth,
    borderStyle: chrome.borderWidth ? 'solid' : undefined,
    borderRadius: chrome.borderRadius,
    opacity: chrome.opacity,
    padding,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    justifyContent: resolveVerticalJustifyContent(chrome.verticalAlign),
    boxShadow:
      chrome.elevation === 2
        ? '0 4px 10px rgba(0,0,0,0.18)'
        : chrome.elevation === 1
          ? '0 2px 4px rgba(0,0,0,0.12)'
          : undefined,
  };
  return { style, alignItems: resolveTextAlignItems(chrome.textAlign) };
}
